import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const TabStorageContext = createContext<{
  storageValue: StorageValue;
  updateTabStorage: (value: StorageValue) => void;
}>({storageValue: {}, updateTabStorage: () => void 0});

enum MessageTypes {
  tabOpened,
  valueUpdate,
}

interface StorageValue {
  heroNotificationShown?: boolean;
}

export const useTabStorage = () => useContext(TabStorageContext);

export const TabStorageProvider = ({children}: PropsWithChildren) => {
  const [storageValue, setStorageValue] = useState<StorageValue>({});
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const tabOpenedRequestSent = useRef<boolean>(false);

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }

    const bc = new BroadcastChannel('AY_Tab_storage');

    bc.onmessage = value => {
      if (value.data.type === MessageTypes.tabOpened) {
        bc.postMessage({
          type: MessageTypes.valueUpdate,
          data: storageValue,
        });
        return;
      }

      if (
        value.data.type === MessageTypes.valueUpdate &&
        Object.keys(value.data.data)
      ) {
        const newValue = {...storageValue, ...value.data.data};
        // eslint-disable-next-line local-rules/set-state-in-use-effect
        setStorageValue(newValue);
      }
    };

    if (!tabOpenedRequestSent.current) {
      bc.postMessage({type: MessageTypes.tabOpened});
    }

    tabOpenedRequestSent.current = true;
    broadcastChannelRef.current = bc;

    return () => {
      broadcastChannelRef.current = null;
      bc.close();
    };
  }, [storageValue]);

  const updateTabStorage = useCallback(
    (value: StorageValue) => {
      const newValue = {...storageValue, ...value};
      setStorageValue(newValue);

      broadcastChannelRef.current?.postMessage({
        type: MessageTypes.valueUpdate,
        data: newValue,
      });
    },
    [storageValue],
  );

  const value = useMemo(() => {
    return {
      storageValue,
      updateTabStorage,
    };
  }, [storageValue, updateTabStorage]);

  return (
    <TabStorageContext.Provider value={value}>
      {children}
    </TabStorageContext.Provider>
  );
};
